import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import * as subscriptions from '../graphql/subscriptions';
import type {
  Job,
  Loan,
  JobConnection,
  LoanConnection,
  FileConnection,
  DocumentConnection,
  Document,
  CreateJobResponse,
  JobSubmissionResponse,
  CreateJobInput,
  SubmitJobInput,
  CreateLoanInput,
  JobType,
  GuidelineRecord,
  DeleteFileResponse,
  DeleteDocumentResponse,
} from '../graphql/types';

const client = generateClient();

export const graphqlClient = {
  queries: {
    async getJob(jobId: string, tenantId: string): Promise<Job | null> {
      const result = await client.graphql({
        query: queries.getJob,
        variables: { jobId, tenantId },
      }) as GraphQLResult<{ getJob: Job }>;
      return result.data?.getJob || null;
    },

    async listJobsByUser(
      tenantId: string,
      username: string,
      limit?: number,
      nextToken?: string,
      archivedOnly?: boolean
    ): Promise<JobConnection> {
      const result = await client.graphql({
        query: queries.listJobsByUser,
        variables: { tenantId, username, limit, nextToken, archivedOnly },
      }) as GraphQLResult<{ listJobsByUser: JobConnection }>;
      return result.data?.listJobsByUser || { items: [] };
    },

    async getLoan(loanId: string, jobId: string, tenantId: string): Promise<Loan | null> {
      const result = await client.graphql({
        query: queries.getLoan,
        variables: { loanId, jobId, tenantId },
      }) as GraphQLResult<{ getLoan: Loan }>;
      return result.data?.getLoan || null;
    },

    async listLoansByJob(
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<LoanConnection> {
      const result = await client.graphql({
        query: queries.listLoansByJob,
        variables: { jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listLoansByJob: LoanConnection }>;
      return result.data?.listLoansByJob || { items: [] };
    },

    async listFilesByLoan(
      loanId: string,
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<FileConnection> {
      const result = await client.graphql({
        query: queries.listFilesByLoan,
        variables: { loanId, jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listFilesByLoan: FileConnection }>;
      return result.data?.listFilesByLoan || { items: [] };
    },

    async listFilesByJob(
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<FileConnection> {
      const result = await client.graphql({
        query: queries.listFilesByJob,
        variables: { jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listFilesByJob: FileConnection }>;
      return result.data?.listFilesByJob || { items: [] };
    },

    async listDocumentsByFile(
      fileId: string,
      loanId: string,
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<DocumentConnection> {
      const result = await client.graphql({
        query: queries.listDocumentsByFile,
        variables: { fileId, loanId, jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listDocumentsByFile: DocumentConnection }>;
      return result.data?.listDocumentsByFile || { items: [] };
    },

    async listDocumentsByLoan(
      loanId: string,
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<DocumentConnection> {
      const result = await client.graphql({
        query: queries.listDocumentsByLoan,
        variables: { loanId, jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listDocumentsByLoan: DocumentConnection }>;
      return result.data?.listDocumentsByLoan || { items: [] };
    },

    async listDocumentsByJob(
      jobId: string,
      tenantId: string,
      limit?: number,
      nextToken?: string
    ): Promise<DocumentConnection> {
      const result = await client.graphql({
        query: queries.listDocumentsByJob,
        variables: { jobId, tenantId, limit, nextToken },
      }) as GraphQLResult<{ listDocumentsByJob: DocumentConnection }>;
      return result.data?.listDocumentsByJob || { items: [] };
    },

    async getDocumentSplitUrl(
      documentId: string,
      jobId: string
    ): Promise<string> {
      const result = await client.graphql({
        query: queries.getDocumentSplitUrl,
        variables: { documentId, jobId },
      }) as GraphQLResult<{ getDocumentSplitUrl: string }>;
      if (!result.data?.getDocumentSplitUrl) {
        throw new Error('No URL returned');
      }
      return result.data.getDocumentSplitUrl;
    },

    async listGuidelines(
      tenantId: string,
      type?: string
    ): Promise<GuidelineRecord[]> {
      const result = await client.graphql({
        query: queries.listGuidelines,
        variables: { tenantId, type },
      }) as GraphQLResult<{ listGuidelines: GuidelineRecord[] }>;
      return result.data?.listGuidelines || [];
    },

    async getLoanBookmarkedPdfUrl(
      jobId: string,
      loanId: string
    ): Promise<string> {
      const result = await client.graphql({
        query: queries.getLoanBookmarkedPdfUrl,
        variables: { jobId, loanId },
      }) as GraphQLResult<{ getLoanBookmarkedPdfUrl: string }>;
      if (!result.data?.getLoanBookmarkedPdfUrl) {
        throw new Error('No URL returned');
      }
      return result.data.getLoanBookmarkedPdfUrl;
    },
  },

  mutations: {
    async createJob(input: CreateJobInput): Promise<CreateJobResponse> {
      const result = await client.graphql({
        query: mutations.createJob,
        variables: { input },
      }) as GraphQLResult<{ createJob: CreateJobResponse }>;
      if (!result.data?.createJob) {
        throw new Error('Failed to create job');
      }
      return result.data.createJob;
    },

    async submitJob(input: SubmitJobInput): Promise<JobSubmissionResponse> {
      const result = await client.graphql({
        query: mutations.submitJob,
        variables: { input },
      }) as GraphQLResult<{ submitJob: JobSubmissionResponse }>;
      if (!result.data?.submitJob) {
        throw new Error('Failed to submit job');
      }
      return result.data.submitJob;
    },

    async updateJobName(jobId: string, tenantId: string, name: string): Promise<Job> {
      const result = await client.graphql({
        query: mutations.updateJobName,
        variables: { jobId, tenantId, name },
      }) as GraphQLResult<{ updateJobName: Job }>;
      if (!result.data?.updateJobName) {
        throw new Error('Failed to update job name');
      }
      return result.data.updateJobName;
    },

    async createLoan(input: CreateLoanInput): Promise<Loan> {
      const result = await client.graphql({
        query: mutations.createLoan,
        variables: { input },
      }) as GraphQLResult<{ createLoan: Loan }>;
      if (!result.data?.createLoan) {
        throw new Error('Failed to create loan');
      }
      return result.data.createLoan;
    },

    async updateLoanInfo(loanId: string, jobId: string, tenantId: string, loanNumber?: string, borrowerName?: string, propertyAddress?: string, loanAmount?: string): Promise<Loan> {
      const result = await client.graphql({
        query: mutations.updateLoanInfo,
        variables: { loanId, jobId, tenantId, loanNumber, borrowerName, propertyAddress, loanAmount },
      }) as GraphQLResult<{ updateLoanInfo: Loan }>;
      if (!result.data?.updateLoanInfo) {
        throw new Error('Failed to update loan info');
      }
      return result.data.updateLoanInfo;
    },

    async deleteJob(jobId: string, tenantId: string): Promise<{ jobId: string; message: string }> {
      const result = await client.graphql({
        query: mutations.deleteJob,
        variables: { jobId, tenantId },
      }) as GraphQLResult<{ deleteJob: { jobId: string; message: string } }>;
      if (!result.data?.deleteJob) {
        throw new Error('Failed to delete job');
      }
      return result.data.deleteJob;
    },

    async deleteDocument(
      documentId: string,
      fileId: string,
      loanId: string,
      jobId: string,
      tenantId: string,
    ): Promise<{ documentId: string; fileId: string; loanId: string; jobId: string; message: string }> {
      const result = await client.graphql({
        query: mutations.deleteDocument,
        variables: { documentId, fileId, loanId, jobId, tenantId },
      }) as GraphQLResult<{
        deleteDocument: {
          documentId: string;
          fileId: string;
          loanId: string;
          jobId: string;
          message: string;
        };
      }>;
      if (!result.data?.deleteDocument) {
        throw new Error('Failed to delete document');
      }
      return result.data.deleteDocument;
    },

    /**
     * Archive a job by setting its status to ARCHIVED.
     * Archived jobs are hidden from list queries but preserved in the database.
     */
    async archiveJob(jobId: string, tenantId: string): Promise<Job> {
      const result = await client.graphql({
        query: mutations.updateJobStatus,
        variables: { jobId, tenantId, status: 'ARCHIVED' },
      }) as GraphQLResult<{ updateJobStatus: Job }>;
      if (!result.data?.updateJobStatus) {
        throw new Error('Failed to archive job');
      }
      return result.data.updateJobStatus;
    },

    /**
     * Set a job's status to an arbitrary JobStatus value. Used to restore
     * archived jobs back to an active status.
     */
    async setJobStatus(jobId: string, tenantId: string, status: string): Promise<Job> {
      const result = await client.graphql({
        query: mutations.updateJobStatus,
        variables: { jobId, tenantId, status },
      }) as GraphQLResult<{ updateJobStatus: Job }>;
      if (!result.data?.updateJobStatus) {
        throw new Error('Failed to update job status');
      }
      return result.data.updateJobStatus;
    },

    /**
     * Delete a single uploaded file (and its nested documents) from a loan.
     * Removes the nested file entry from the job item in DynamoDB.
     */
    async deleteFile(
      fileId: string, loanId: string, jobId: string, tenantId: string, s3Key?: string
    ): Promise<DeleteFileResponse> {
      const result = await client.graphql({
        query: mutations.deleteFile,
        variables: { fileId, loanId, jobId, tenantId, s3Key },
      }) as GraphQLResult<{ deleteFile: DeleteFileResponse }>;
      if (!result.data?.deleteFile) {
        throw new Error('Failed to delete file');
      }
      return result.data.deleteFile;
    },

    /**
     * Delete a single extracted document from a file.
     * Removes the nested document entry from the job item in DynamoDB.
     */
    async deleteDocument(
      documentId: string, fileId: string, loanId: string, jobId: string, tenantId: string
    ): Promise<DeleteDocumentResponse> {
      const result = await client.graphql({
        query: mutations.deleteDocument,
        variables: { documentId, fileId, loanId, jobId, tenantId },
      }) as GraphQLResult<{ deleteDocument: DeleteDocumentResponse }>;
      if (!result.data?.deleteDocument) {
        throw new Error('Failed to delete document');
      }
      return result.data.deleteDocument;
    },

    async finalizeDocumentReview(
      fileId: string, loanId: string, jobId: string,
      tenantId: string, extractedFields: string
    ): Promise<{ fileId: string; loanId: string; jobId: string; status: string }> {
      const result = await client.graphql({
        query: mutations.finalizeDocumentReview,
        variables: { input: { fileId, loanId, jobId, tenantId, extractedFields } },
      }) as GraphQLResult<{ finalizeDocumentReview: { fileId: string; loanId: string; jobId: string; status: string } }>;
      if (!result.data?.finalizeDocumentReview) {
        throw new Error('Failed to finalize document review');
      }
      return result.data.finalizeDocumentReview;
    },
  },

  subscriptions: {
    /**
     * Subscribe to job status changes filtered by job type
     * Requirements: 12.1, 12.6 - Real-time updates with job type filtering
     */
    onJobStatusChanged(tenantId: string, username: string, jobType?: JobType) {
      return client.graphql({
        query: subscriptions.onJobStatusChanged,
        variables: { tenantId, username, jobType },
      });
    },

    onLoanStatusChanged(jobId: string, tenantId: string) {
      return client.graphql({
        query: subscriptions.onLoanStatusChanged,
        variables: { jobId, tenantId },
      });
    },

    onFileStatusChanged(loanId: string, jobId: string, tenantId: string) {
      return client.graphql({
        query: subscriptions.onFileStatusChanged,
        variables: { loanId, jobId, tenantId },
      });
    },

    onDocumentStatusChanged(loanId: string, jobId: string, tenantId: string) {
      return client.graphql({
        query: subscriptions.onDocumentStatusChanged,
        variables: { loanId, jobId, tenantId },
      });
    },
  },
};
